import isMobile from 'is-mobile';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Colapse } from '../../assets';
import {
  FutureListDark,
  FutureListMobile,
  FutureListMobile1,
  HomeIconMobile,
  PlaylistDark,
  SearchIconMobile,
} from '../../assets/index';
import { SPOTIFY_TOKEN } from '../../constants/storage';
import { useLoginContext } from '../../context';
import { usePlaylistContext } from '../../context/PlaylistContext';
import SpotifyApiService from '../../services/SpotifyApiService';
import { addItemToStorage, getRefreshedToken, isEmptyOrNil, truncateText } from '../../utils';
import AudioPlayerTool from './AudioPlayerTool';
import CreatePlayListModal from '../../pages/Playlist/CreatePlayListModal';

import { SideBarTabStyle } from '../SideBar/SideBar.style';
import { DrawerStyle } from '../ToggledMenu/ToggledMenu.style';
import { AudioPlayerStyle, AudioPlayerStyle1, MobilePlaylistDrawerStyle } from './AudioPlayer.style';
import 'react-h5-audio-player/lib/styles.css';

// Custom hook for localStorage persistence
const useLocalStorage = (key: string, initialValue: boolean) => {
  const [storedValue, setStoredValue] = useState<boolean>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  const setValue = (value: boolean | ((val: boolean) => boolean)) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  };

  return [storedValue, setValue] as const;
};

const AudioPlayer = () => {
  const [isRotateDesk, setRotateDesk] = useState('rotateY(0deg)');
  const [showEvery, setEvery] = useLocalStorage('audioPlayerShowEvery', true);
  const { state } = usePlaylistContext();
  const { playlistId } = useParams();
  const navigate = useNavigate();

  const [theme, setTheme] = useState('dark');

  useEffect(() => {
    setTheme(state?.isWholeTheme === false ? 'dark' : 'light');
  }, [state]);

  const [currentPage, setCurrentPage] = useState(1);

  const { dispatch: newDispatch } = usePlaylistContext();
  const { dispatch: loginDispatch } = useLoginContext();
  
  // Mobile playlist drawer states
  const [playlists, setPlaylists] = useState([]);
  const [totalPlaylist, setTotalPlaylist] = useState(0);
  const [isLoadingPlaylists, setIsLoadingPlaylists] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchedPlaylists, setSearchedPlaylists] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [showCreatePlaylist, setShowCreatePlaylist] = useState(false);

  // Load playlists
  const loadPlaylists = (loadMore = false) => {
    setIsLoadingPlaylists(true);
    const offset = loadMore ? playlists.length : 0;
    
    SpotifyApiService.getInstance()
      .getUserPlaylists(20, offset)
      .then(response => {
        setTotalPlaylist(response?.data.total);
        if (loadMore) {
          setPlaylists([...playlists, ...response?.data.items]);
        } else {
          setPlaylists(response?.data.items);
        }
        setIsLoadingPlaylists(false);
      })
      .catch(async error => {
        if (error.response?.data?.error?.status === 401 || error.response?.status === 400) {
          getRefreshedToken(state.refreshToken)
            .then(response => {
              addItemToStorage(SPOTIFY_TOKEN, response?.data?.access_token);
              loginDispatch({
                type: 'ADD_TOKEN',
                payload: response?.data?.access_token,
              });
            })
            .catch(() => {});
        }
        setIsLoadingPlaylists(false);
      });
  };

  // Search playlists
  const searchPlaylists = async (term: string) => {
    if (!term.trim()) {
      setSearchedPlaylists([]);
      setHasSearched(false);
      return;
    }

    setIsSearching(true);
    setHasSearched(true);
    try {
      const response = await SpotifyApiService.getInstance().searchPlaylists(term, 20);
      if (response.data.playlists && response.data.playlists.items) {
        const currentUser = JSON.parse(localStorage.getItem('spotifyUser'));
        const userPlaylists = response.data.playlists.items.filter(
          playlist => playlist.owner?.display_name === currentUser?.username
        );
        setSearchedPlaylists(userPlaylists);
      }
    } catch (error) {
      console.error('Error searching playlists:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const CurrentPages = () => {
    SpotifyApiService.getInstance()
      .getUserPlaylists(40, (currentPage - 1) * 3)
      .then(genreResponse => {
        newDispatch({
          type: 'INITIALIZE_PLAYLISTS',
          payload: genreResponse?.data.items,
        });
      })
      .catch(async error => {
        setCurrentPage(1);
        if (error.response?.data?.error?.status === 401 || error.response?.status === 400) {
          getRefreshedToken(state.refreshToken)
            .then(response => {
              addItemToStorage(SPOTIFY_TOKEN, response?.data?.access_token);
              loginDispatch({
                type: 'ADD_TOKEN',
                payload: response?.data?.access_token,
              });
            })
            .catch(() => {});
        }
      });
  };

  const onHide = () => {
    setEvery(false);
    setRotateDesk('rotateY(180deg)');
  };

  const onFull = () => {
    setEvery(true);
    setRotateDesk('rotateY(0deg)');
  };

  const [showDrawer, setShowDrawer] = useState(false);

  const handleOpen = () => {
    setShowDrawer(true);
    if (playlists.length === 0) {
      loadPlaylists();
    }
  };

  const handleClose = () => {
    setShowDrawer(false);
    setSearchTerm('');
    setSearchedPlaylists([]);
    setHasSearched(false);
  };

  const handlePlaylistClick = (id: string) => {
    handleClose();
    navigate(`/playlist/${id}`);
  };

  type SideBarTabProps = {
    title: string;
    icon?: React.ReactNode;
    className?: string;
    url?: string;
    onClick?: () => void;
    isActive?: boolean;
  };

  const SideBarTab = (props: SideBarTabProps) => {
    const { title, icon, className, url, onClick, isActive } = props;

    const navigate = useNavigate();

    const handleTabClick = () => {
      if (!isEmptyOrNil(onClick)) {
        onClick();
        return;
      }

      if (!url) return;

      navigate(url);
    };

    return (
      <SideBarTabStyle
        className={`${className} ${isActive ? 'active' : ''}`}
        onClick={handleTabClick}
      >
        {icon}
        <p className="title" title={title}>
          {title}
        </p>
      </SideBarTabStyle>
    );
  };

  return (
    <>
      <AudioPlayerStyle1>
        {showEvery === true ? (
          isMobile() === true ? (
            <></>
          ) : (
            <span className="spanner" onClick={onHide}>
              <div style={{ transform: `${isRotateDesk}` }}>
                <Colapse />
              </div>
            </span>
          )
        ) : isMobile() === true ? (
          <></>
        ) : (
          <span className="spanner" onClick={onFull}>
            <div style={{ transform: `${isRotateDesk}` }}>
              <Colapse />
            </div>
          </span>
        )}
        <div className="div">
          <div
            className={`AudioPlayerpreviewSongStyleclassName ${showEvery ? ' active' : ''} ${
              isMobile() && state.previewSong.name ? 'SongPlaying' : ''
            }`}
          >
            <AudioPlayerStyle>
              <div className={!showEvery ? `SongSideBar` : `audioPlayerInner`}>
                {showEvery !== true ? (
                  <div className="SongSideBarImage">
                    {state.previewSong?.image ? (
                      <img
                        alt="songImage"
                        src={state.previewSong?.image}
                        className="AudioPlayerStyleClassNameImageTwo"
                      />
                    ) : (
                      <img
                        alt="songImage"
                        src="./static/background1.png"
                        className="AudioPlayerStyleClassNameImage"
                      />
                    )}
                  </div>
                ) : (
                  <>
                    <div className={!showEvery ? `SongSideBarImage` : 'AudioPlayerStyleClassName'}>
                      {state.previewSong?.image ? (
                        <img
                          alt="songImage"
                          src={state.previewSong?.image}
                          className="AudioPlayerStyleClassNameImageTwo"
                        />
                      ) : (
                        <img
                          alt="songImage"
                          src="./static/background1.png"
                          className="AudioPlayerStyleClassNameImage"
                        />
                      )}
                      {isMobile() === false ? (
                        <div
                          className={!showEvery ? `songDetail` : 'AudioPlayerStyleClassNameDiv'}
                          style={{ display: 'block' }}
                        >
                          {/* <p className="titler">Title</p> */}
                          <p style={{}} className="p2">
                            {state.previewSong?.name ? state.previewSong?.name : 'No song selected'}
                          </p>
                          <p style={{}} className="p4">
                            {state.previewSong?.artist
                              ? state.previewSong?.artist
                              : 'No song selected'}
                          </p>
                        </div>
                      ) : (
                        ''
                      )}
                    </div>
                  </>
                )}
                <div className="audioPlayer">
                  {showEvery === true ? (
                    isMobile() === true ? (
                      <p className="pp12">
                        {state.previewSong?.name ? state.previewSong?.name : 'No song selected'}
                      </p>
                    ) : (
                      ''
                    )
                  ) : (
                    ''
                  )}

                  <div className={!showEvery ? 'songDetail' : ''}>
                    {!showEvery ? (
                      <>
                        <h2>{state.previewSong?.name ? state.previewSong?.name : `Play a song`}</h2>
                        <p>{state.previewSong?.artist ? state.previewSong?.artist : `artist`}</p>
                      </>
                    ) : null}
                    <AudioPlayerTool showFilledVolume={false} />
                  </div>
                </div>
              </div>
            </AudioPlayerStyle>
          </div>
          <div className="AudioPlayerStyleClassNameFlexbox">
            <div className="AudioPlayerFlexbox">
              <SideBarTab
                className="tab"
                icon={
                  theme === 'dark' ? (
                    <HomeIconMobile color={'white'} />
                  ) : (
                    <HomeIconMobile color={'#1D1D1D'} />
                  )
                }
                title="Home"
                url="/home"
              />
            </div>
            <div className="AudioPlayerFlexbox">
              <SideBarTab
                className="tab"
                icon={
                  theme === 'dark' ? (
                    <SearchIconMobile color={'white'} />
                  ) : (
                    <SearchIconMobile color={'#1D1D1D'} />
                  )
                }
                title="Browse"
                url="/search"
              />
            </div>
            <div className="AudioPlayerFlexbox">
              <Link to="/future_playlist" className="paragraphing tab">
                <span>
                  {' '}
                  {theme === 'dark' ? <FutureListMobile color={'white'} /> : <FutureListDark />}
                </span>{' '}
                <span style={{}} className="title1">
                  Pre-release
                </span>
              </Link>
            </div>
            <DrawerStyle
              // title="Drawer with extra actions"
              placement="left"
              onClose={handleClose}
              visible={showDrawer}
              width={300}
            >
              <div className="playlist-items">
                {state.playlists.map(data => {
                  const { name, id } = data;

                  return (
                    <div
                      className={`playlist-item ${playlistId === id ? 'active' : ''}`}
                      onClick={handleClose}
                    >
                      <Link to={`/playlist/${id}`} className="titles1">
                        <p>{name}</p>
                      </Link>
                    </div>
                  );
                })}
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <p
                    onClick={() => {
                      setCurrentPage(currentPage + 1);
                      CurrentPages();
                    }}
                    style={{
                      background: 'rgb(0, 122, 255,0.6)',
                      marginTop: '5px',
                      marginBottom: '5px',
                      padding: '3px',
                      borderRadius: '5px',
                      width: '50%',
                      textAlign: 'center',
                      cursor: 'pointer',
                      color: 'white',
                    }}
                  >
                    Load more
                  </p>
                </div>
              </div>
            </DrawerStyle>
            <div className="AudioPlayerFlexbox">
              <SideBarTab
                className="settings-button tab"
                icon={theme === 'dark' ? <FutureListMobile1 color={'white'} /> : <PlaylistDark />}
                title="Playlists"
                onClick={handleOpen}
              />
            </div>
          </div>
          <div className="bottomNav">
            <Link to="/home" className="bottomBarNav">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                <polyline points="9 22 9 12 15 12 15 22"></polyline>
              </svg>
              <span>Home</span>
            </Link>
            <Link to="/search" className="bottomBarNav">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <span>Browse</span>
            </Link>
            <Link to="/future_playlist" className="bottomBarNav">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              <span>Pre-release</span>
            </Link>
            <Link to="/schedule_tracks" className="bottomBarNav">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              <span>Scheduled</span>
            </Link>
            <div className="bottomBarNav" onClick={handleOpen}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18V5l12-2v13"></path>
                <circle cx="6" cy="18" r="3"></circle>
                <circle cx="18" cy="16" r="3"></circle>
              </svg>
              <span>Playlists</span>
            </div>
            
            {/* Mobile Playlist Drawer */}
            <MobilePlaylistDrawerStyle
              placement="left"
              onClose={handleClose}
              visible={showDrawer}
              width="85%"
              closable={false}
            >
              <div className="drawer-content">
                {/* Header */}
                <div className="drawer-header">
                  <h2>My Playlists</h2>
                  <button className="close-btn" onClick={handleClose}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>

                {/* Actions Row */}
                <div className="actions-row">
                  <button className="action-btn create-btn" onClick={() => setShowCreatePlaylist(true)}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                    <span>Create</span>
                  </button>
                </div>

                {/* Search Input */}
                <div className="search-container">
                  <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                  <input
                    type="text"
                    placeholder="Search playlists..."
                    value={searchTerm}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSearchTerm(value);
                      if (!value.trim()) {
                        setSearchedPlaylists([]);
                        setHasSearched(false);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        searchPlaylists(searchTerm);
                      }
                    }}
                  />
                  {searchTerm && (
                    <button 
                      className="clear-search" 
                      onClick={() => {
                        setSearchTerm('');
                        setSearchedPlaylists([]);
                        setHasSearched(false);
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  )}
                </div>

                {/* Playlist List */}
                <div className="playlist-list">
                  {isSearching && (
                    <div className="loading-state">
                      <div className="spinner"></div>
                      <span>Searching...</span>
                    </div>
                  )}

                  {!isSearching && searchTerm && hasSearched && searchedPlaylists.length === 0 && (
                    <div className="empty-state">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      </svg>
                      <p>No playlists found</p>
                    </div>
                  )}

                  {!isSearching && (searchTerm && searchedPlaylists.length > 0 ? searchedPlaylists : playlists).map((playlist: any) => {
                    const currentUser = JSON.parse(localStorage.getItem('spotifyUser'));
                    const isOwner = playlist?.owner?.display_name === currentUser?.username;
                    
                    if (!searchTerm && !isOwner) return null;
                    
                    return (
                      <div
                        key={playlist.id}
                        className={`playlist-item ${playlistId === playlist.id ? 'active' : ''}`}
                        onClick={() => handlePlaylistClick(playlist.id)}
                      >
                        <div className="playlist-image">
                          {playlist.images?.[0]?.url ? (
                            <img src={playlist.images[0].url} alt={playlist.name} />
                          ) : (
                            <div className="placeholder-image">
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M9 18V5l12-2v13"></path>
                                <circle cx="6" cy="18" r="3"></circle>
                                <circle cx="18" cy="16" r="3"></circle>
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="playlist-info">
                          <span className="playlist-name">{truncateText(playlist.name, 28)}</span>
                          <span className="playlist-tracks">{playlist.tracks?.total || 0} tracks</span>
                        </div>
                        {playlistId === playlist.id && (
                          <div className="active-indicator">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {isLoadingPlaylists && (
                    <div className="loading-state">
                      <div className="spinner"></div>
                      <span>Loading playlists...</span>
                    </div>
                  )}

                  {!searchTerm && !isLoadingPlaylists && playlists.length < totalPlaylist && (
                    <button className="load-more-btn" onClick={() => loadPlaylists(true)}>
                      Load more playlists
                    </button>
                  )}
                </div>
              </div>
            </MobilePlaylistDrawerStyle>

            {/* Create Playlist Modal */}
            <CreatePlayListModal
              isModalVisible={showCreatePlaylist}
              setIsModalVisible={setShowCreatePlaylist}
              playlists={playlists}
              setPlaylists={setPlaylists}
            />
          </div>
        </div>
      </AudioPlayerStyle1>
    </>
  );
};

export default AudioPlayer;
